import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { 
  CalendarPlus, Calendar as CalendarIcon, Clock, MapPin, Video, User,
  CheckCircle, XCircle, Edit, Trash2, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  rescheduled: { label: 'Rescheduled', color: 'bg-amber-100 text-amber-700' },
  no_show: { label: 'No Show', color: 'bg-red-100 text-red-700' },
};

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30'
];

export default function VivaSchedulingTab({ token }) {
  const [schedules, setSchedules] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [examiners, setExaminers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);

  const [newSchedule, setNewSchedule] = useState({
    candidate_id: '',
    examiner_id: '',
    second_examiner_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    location: '',
    meeting_link: '',
    examination_code: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch schedules
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const [schedulesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/viva-schedules${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSchedules(schedulesRes.data);
      
      // Filter users by role
      const users = usersRes.data;
      setCandidates(users.filter(u => u.role === 'candidate' || u.role === 'user'));
      setExaminers(users.filter(u => u.role === 'examiner' || u.role === 'admin'));
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.candidate_id || !newSchedule.examiner_id || !newSchedule.scheduled_date || !newSchedule.scheduled_time) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/viva-schedules`, newSchedule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Viva schedule created successfully');
      setShowCreateDialog(false);
      setNewSchedule({
        candidate_id: '', examiner_id: '', second_examiner_id: '',
        scheduled_date: '', scheduled_time: '', duration_minutes: 30,
        location: '', meeting_link: '', examination_code: '', notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleStatusChange = async (scheduleId, status) => {
    try {
      await axios.put(`${API}/admin/viva-schedules/${scheduleId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await axios.delete(`${API}/admin/viva-schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Schedule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  // Group schedules by date for calendar view
  const schedulesByDate = schedules.reduce((acc, s) => {
    const date = s.scheduled_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="viva-scheduling-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#1B2A4A]">Viva Scheduling</h2>
          <p className="text-sm text-slate-500">Schedule and manage viva voce examinations</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B2A4A] text-white rounded-sm">
              <CalendarPlus className="h-4 w-4 mr-2" /> Schedule Viva
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Schedule Viva Examination</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Candidate *</Label>
                <Select 
                  value={newSchedule.candidate_id} 
                  onValueChange={v => setNewSchedule({...newSchedule, candidate_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select candidate" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Primary Examiner *</Label>
                <Select 
                  value={newSchedule.examiner_id} 
                  onValueChange={v => setNewSchedule({...newSchedule, examiner_id: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select examiner" />
                  </SelectTrigger>
                  <SelectContent>
                    {examiners.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Second Examiner (Optional)</Label>
                <Select 
                  value={newSchedule.second_examiner_id || 'none'} 
                  onValueChange={v => setNewSchedule({...newSchedule, second_examiner_id: v === 'none' ? '' : v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select examiner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {examiners.filter(e => e.id !== newSchedule.examiner_id).map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={newSchedule.scheduled_date}
                  onChange={e => setNewSchedule({...newSchedule, scheduled_date: e.target.value})}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Select 
                  value={newSchedule.scheduled_time} 
                  onValueChange={v => setNewSchedule({...newSchedule, scheduled_time: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Select 
                  value={String(newSchedule.duration_minutes)} 
                  onValueChange={v => setNewSchedule({...newSchedule, duration_minutes: parseInt(v)})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 mins</SelectItem>
                    <SelectItem value="30">30 mins</SelectItem>
                    <SelectItem value="45">45 mins</SelectItem>
                    <SelectItem value="60">60 mins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Examination Code</Label>
                <Select 
                  value={newSchedule.examination_code} 
                  onValueChange={v => setNewSchedule({...newSchedule, examination_code: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select exam" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CBEAM">CBEAM</SelectItem>
                    <SelectItem value="CBELM">CBELM</SelectItem>
                    <SelectItem value="CBERM">CBERM</SelectItem>
                    <SelectItem value="CBEAD">CBEAD</SelectItem>
                    <SelectItem value="CBENA">CBENA</SelectItem>
                    <SelectItem value="CBEAA">CBEAA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Location</Label>
                <Input 
                  value={newSchedule.location}
                  onChange={e => setNewSchedule({...newSchedule, location: e.target.value})}
                  className="mt-1"
                  placeholder="Room 101, IMCAC Center or Online"
                />
              </div>
              <div className="col-span-2">
                <Label>Meeting Link (for online vivas)</Label>
                <Input 
                  value={newSchedule.meeting_link}
                  onChange={e => setNewSchedule({...newSchedule, meeting_link: e.target.value})}
                  className="mt-1"
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <textarea 
                  value={newSchedule.notes}
                  onChange={e => setNewSchedule({...newSchedule, notes: e.target.value})}
                  className="w-full mt-1 p-2 border rounded-sm text-sm"
                  rows={2}
                  placeholder="Any special instructions..."
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateSchedule} className="bg-[#1B2A4A] text-white">
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const count = schedules.filter(s => s.status === status).length;
          return (
            <Card 
              key={status} 
              className={`cursor-pointer transition-all ${filterStatus === status ? 'ring-2 ring-[#C5A572]' : ''}`}
              onClick={() => setFilterStatus(filterStatus === status ? 'all' : status)}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#1B2A4A]">{count}</p>
                <Badge className={config.color}>{config.label}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Schedules Table */}
      <Card className="imcac-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading schedules...</div>
          ) : schedules.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No viva schedules found</p>
              <Button 
                className="mt-4 bg-[#C5A572] text-white"
                onClick={() => setShowCreateDialog(true)}
              >
                Schedule First Viva
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Examiner(s)</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map(schedule => {
                  const statusConfig = STATUS_CONFIG[schedule.status] || STATUS_CONFIG.scheduled;
                  return (
                    <TableRow key={schedule.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="font-medium">{formatDate(schedule.scheduled_date)}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {schedule.scheduled_time} ({schedule.duration_minutes}m)
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{schedule.candidate_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{schedule.examiner_name}</p>
                          {schedule.second_examiner_name && (
                            <p className="text-slate-500">+ {schedule.second_examiner_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100">{schedule.examination_code || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          {schedule.meeting_link ? (
                            <>
                              <Video className="h-3 w-3" />
                              <a href={schedule.meeting_link} target="_blank" rel="noopener" className="text-blue-600 hover:underline">
                                Online
                              </a>
                            </>
                          ) : (
                            <>
                              <MapPin className="h-3 w-3" />
                              {schedule.location || 'TBD'}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {schedule.status === 'scheduled' && (
                            <Button 
                              size="sm" variant="ghost"
                              onClick={() => handleStatusChange(schedule.id, 'confirmed')}
                              className="h-8 w-8 p-0 text-green-600"
                              title="Mark Confirmed"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {schedule.status === 'confirmed' && (
                            <Button 
                              size="sm" variant="ghost"
                              onClick={() => handleStatusChange(schedule.id, 'completed')}
                              className="h-8 w-8 p-0 text-blue-600"
                              title="Mark Completed"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" variant="ghost"
                            onClick={() => handleStatusChange(schedule.id, 'cancelled')}
                            className="h-8 w-8 p-0 text-amber-600"
                            title="Cancel"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" variant="ghost"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="h-8 w-8 p-0 text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
